import type { AppState } from './state';
import { initialState, normalizeSettings, SCHEMA_VERSION } from './state';
import type { EventOf, EventType, StudioEvent } from './events';
import { clock } from './util/misc';

/**
 * Event-driven store. A single dispatch runs every registered processor;
 * processors may emit follow-up events, which are processed in the same
 * transaction. Subscribers are notified once per transaction, then UI effect
 * listeners (toasts, level-up overlay…) receive each processed event.
 */

export interface ProcessorContext {
  emit: (e: StudioEvent) => void;
  now: number;
}
export type Processor = (state: AppState, event: StudioEvent, ctx: ProcessorContext) => AppState;

export interface Repository {
  load(): AppState | null;
  save(state: AppState): void;
  clear(): void;
}

export class MemoryRepository implements Repository {
  private data: AppState | null = null;
  load() { return this.data; }
  save(s: AppState) { this.data = s; }
  clear() { this.data = null; }
}

const STORAGE_KEY = 'studio.state.v3';

/** Caps the long histories before persisting (locally or to the account). */
export function trimForStorage(state: AppState, scale = 1): AppState {
  const n = (max: number) => Math.round(max * scale);
  return {
    ...state,
    attempts: state.attempts.slice(-n(3000)),
    xp: { ...state.xp, events: state.xp.events.slice(-n(1500)) },
    notifications: state.notifications.slice(-80),
    reviewLog: state.reviewLog.slice(-n(3000)),
    calibration: { ...state.calibration, log: state.calibration.log.slice(-n(400)) },
  };
}

export class LocalStorageRepository implements Repository {
  private timer: ReturnType<typeof setTimeout> | null = null;
  load(): AppState | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return migrate(JSON.parse(raw) as Partial<AppState>);
    } catch {
      return null;
    }
  }
  save(state: AppState) {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(trimForStorage(state)));
      } catch {
        /* storage full or unavailable — the app keeps working in memory */
      }
    }, 250);
  }
  clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
}

/** Forward-compatible: keep known collections, fill new ones, normalise settings from any version. */
export function migrate(old: Partial<AppState>): AppState {
  return { ...initialState(), ...old, settings: normalizeSettings(old.settings), schema: SCHEMA_VERSION };
}

type Listener = () => void;
type EventListener = (e: StudioEvent, state: AppState) => void;

export class Store {
  private state: AppState;
  private listeners = new Set<Listener>();
  private eventListeners = new Map<string, Set<EventListener>>();
  private processors: Processor[] = [];
  private dispatching = false;
  private pending: StudioEvent[] = [];

  constructor(private repo: Repository, initial?: AppState) {
    this.state = initial ?? repo.load() ?? initialState();
  }

  use(p: Processor) {
    this.processors.push(p);
  }

  getState = () => this.state;

  subscribe = (fn: Listener) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  /** Listen for a processed event (UI effects). */
  on<T extends EventType>(type: T | '*', fn: (e: EventOf<T>, state: AppState) => void) {
    const set = this.eventListeners.get(type) ?? new Set();
    set.add(fn as EventListener);
    this.eventListeners.set(type, set);
    return () => set.delete(fn as EventListener);
  }

  dispatch = (event: StudioEvent) => {
    if (this.dispatching) {
      this.pending.push(event);
      return;
    }
    this.dispatching = true;
    const processed: StudioEvent[] = [];
    const queue: StudioEvent[] = [event];
    let state = this.state;
    let guard = 0;
    while (queue.length && guard++ < 500) {
      const e = queue.shift()!;
      const ctx: ProcessorContext = { emit: (x) => queue.push(x), now: clock.now() };
      for (const p of this.processors) state = p(state, e, ctx);
      processed.push(e);
    }
    this.state = state;
    this.dispatching = false;
    this.repo.save(state);
    this.listeners.forEach((l) => l());
    for (const e of processed) {
      this.eventListeners.get(e.type)?.forEach((fn) => fn(e, state));
      this.eventListeners.get('*')?.forEach((fn) => fn(e, state));
    }
    if (this.pending.length) {
      const next = this.pending.shift()!;
      this.dispatch(next);
    }
  };

  /** Replace the whole state (demo seeding, import, reset). */
  replace(state: AppState) {
    this.state = state;
    this.repo.save(state);
    this.listeners.forEach((l) => l());
  }

  reset() {
    this.repo.clear();
    this.replace(initialState());
  }
}
